import type { AssetId, ChainId } from '@shapeshiftoss/caip'
import { ASSET_NAMESPACE, toAssetId } from '@shapeshiftoss/caip'
import { skipToken, useQuery } from '@tanstack/react-query'

import { OrderDirection } from '@/components/OrderDropdown/types'
import { SortOptionsKeys } from '@/components/SortDropdown/types'
import { PORTALS_NETWORK_TO_CHAIN_ID } from '@/lib/portals/constants'
import type { TokenInfo } from '@/lib/portals/types'
import { fetchPortalsPlatforms, fetchPortalsTokens, portalTokenToAsset } from '@/lib/portals/utils'
import { assets as assetsSlice } from '@/state/slices/assetsSlice/assetsSlice'
import { marketApi } from '@/state/slices/marketDataSlice/marketDataSlice'
import { selectAssets, selectFeeAssetById } from '@/state/slices/selectors'
import { store, useAppDispatch, useAppSelector } from '@/state/store'

export type PortalsAssets = {
  byId: Record<AssetId, TokenInfo>
  ids: AssetId[]
  chainIds: ChainId[]
}

export const usePortalsAssetsQuery = ({
  enabled,
  chainIds,
  sortBy,
  orderBy,
  minApy,
  maxApy,
  tags,
}: {
  enabled: boolean
  chainIds: ChainId[] | undefined
  sortBy?: SortOptionsKeys
  orderBy?: OrderDirection
  minApy?: string
  maxApy?: string
  tags?: string[]
}) => {
  const dispatch = useAppDispatch()
  const assets = useAppSelector(selectAssets)

  const { data: portalsPlatformsData } = useQuery({
    queryKey: ['portalsPlatforms'],
    queryFn: enabled ? () => fetchPortalsPlatforms() : skipToken,
    // This should effectively be considered static as far as the lifecycle of the app/our usage is concerned
    staleTime: Infinity,
  })

  return useQuery({
    queryKey: ['portalsAssets', { chainIds, orderBy, sortBy, minApy, maxApy, tags }],
    queryFn:
      enabled && portalsPlatformsData
        ? () =>
            fetchPortalsTokens({
              totalLimit: 10,
              chainIds,
              sortBy: sortBy === SortOptionsKeys.Volume ? 'volumeUsd1d' : 'apy',
              minApy,
              maxApy,
              sortDirection:
                orderBy === OrderDirection.Ascending && sortBy !== SortOptionsKeys.MarketCap
                  ? 'asc'
                  : 'desc',
              tags,
            })
        : skipToken,
    gcTime: 60 * 1000 * 5,
    staleTime: 60 * 1000 * 5,
    select: tokens => {
      if (!portalsPlatformsData) return
      if (!enabled) return

      return tokens.reduce<PortalsAssets>(
        (acc, token) => {
          const chainId = PORTALS_NETWORK_TO_CHAIN_ID[token.network]
          if (!chainId) return acc

          const assetId = toAssetId({
            chainId,
            assetNamespace: ASSET_NAMESPACE.erc20,
            assetReference: token.address,
          })
          const feeAsset = selectFeeAssetById(store.getState(), assetId)
          if (!feeAsset) return acc

          const asset = portalTokenToAsset({
            token,
            portalsPlatforms: portalsPlatformsData,
            nativeAsset: feeAsset,
            chainId,
          })

          if (!asset) return acc
          if (!token.liquidity) return acc

          // upsert fetched asset if doesn't exist in generatedAssetData.json
          if (!assets[assetId]) {
            dispatch(
              assetsSlice.actions.upsertAssets({
                ids: [assetId],
                byId: { [assetId]: asset },
              }),
            )
          }

          // and fetch its market-data since it may or may not be missing
          dispatch(marketApi.endpoints.findByAssetId.initiate(assetId))

          if (!acc.chainIds.includes(chainId)) {
            acc.chainIds.push(chainId)
          }

          acc.byId[assetId] = token
          acc.ids.push(assetId)
          return acc
        },
        {
          byId: {},
          ids: [],
          chainIds: [],
        },
      )
    },
  })
}
